// ANT Design
import {
  BorderOuterOutlined,
} from '@ant-design/icons';
import { InputNumber, Select, Checkbox, Col, Form, Row, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { ColorPaletteButton } from './ColorPaletteButton';
import DuotoneLightIcon from './DuotoneLightIcon';
import TitleFontSelector from './TitleFontSelector';
import './Utils.css';

import { marginOnPageButton, maxFontSize, maxTitleFontSize, minFontSize, pageWidth, selectorMaxWidth, unitName } from '../../constants/gridConfig';
import { DEFAULT_TITLE_INDEX_STYLE, DEFAULT_TITLE_TEXT_EFFECT } from '../Title/titleUtils';
import FontPicker from './FontPicker';
import { iconButtonStyle } from '../../utils/styles';
import {
  FieldGroupTitle,
  MODAL_FIELD_WIDTH,
  MODAL_LABEL_WIDTH,
  modalFieldLayout,
} from '../RightSide/configurationModal/fieldLayout';
import { headerIndentation, numberedTitleLevels } from '../../utils/titles';
import { useStore } from '../../store/store';
import { useConfigCompact } from '../../contexts/ConfigCompactContext';
import { BORDER_GRAY, borderIcons } from '../../constants/constants';

const selector = ({ configuration }) => ({
  configuration,
});

const indicators = {
  letterMin: 'a',
  letterMaj: 'A',
  chiffre: '1',
  chiffreZero: '01',
  chiffreDoubleZero: '001',
  romain: 'I',
  '': '',
};

export const handleTransformIndicator = (indicator) => {
  for (const [key, value] of Object.entries(indicators)) {
    if (key === indicator) return value;
  }
};

const styleButtonStyle = (active) => ({
  cursor: 'pointer',
  textAlign: 'center',
  fontSize: 14,
  fontFamily: 'monospace',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: 4,
  border: '1px solid lightgray',
  marginRight: 4,
  color: active ? '#5890ff' : 'inherit',
  borderColor: active ? '#5890ff' : 'lightgray',
});

const alignButtonStyle = (active) => ({
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: 4,
  border: '1px solid lightgray',
  marginRight: 4,
  color: active ? '#5890ff' : 'inherit',
  borderColor: active ? '#5890ff' : 'lightgray',
});

/**
 * Boutons G / I / S d'un style de texte, et son alignement au besoin.
 *
 * Hors `Form.Item` : les onglets des styles d'éléments les posent dans une
 * rangée de panneau de box (LabelledRow), pas dans une rangée de formulaire.
 */
export const TextStyleButtons = ({
  rules: styleRules,
  changeHandler,
  showAlignment,
  onChangeAlignment,
  disabled,
}) => {
  const { t } = useTranslation();
  // Un style verrouillé ne se clique pas, et se voit.
  const press = (handler) => (disabled ? undefined : handler);
  const buttonStyle = (active) => ({
    ...styleButtonStyle(active),
    ...(disabled ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
  });

  return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <div
          title={t('bold')}
          style={{ ...buttonStyle(Boolean(styleRules.fontWeight)), fontWeight: 'bold' }}
          onClick={press(changeHandler.fontWeight)}
        >
          G
        </div>
        <div
          title={t('italic')}
          style={{ ...buttonStyle(Boolean(styleRules.fontStyle)), fontStyle: 'italic' }}
          onClick={press(changeHandler.fontStyle)}
        >
          I
        </div>
        <div
          title={t('underline')}
          style={{ ...buttonStyle(Boolean(styleRules.textDecoration)), textDecoration: 'underline' }}
          onClick={press(changeHandler.textDecoration)}
        >
          S
        </div>
        {showAlignment && (
          <>
            <div style={{ width: 8 }} />
            <div
              title={t('left-alignment')}
              style={alignButtonStyle(styleRules.textAlign === 'left')}
              onClick={() => onChangeAlignment('left')}
            >
              <FontAwesomeIcon icon={icon({ name: 'align-left', style: 'light' })} style={{ fontSize: 14 }} />
            </div>
            <div
              title={t('center-alignment')}
              style={alignButtonStyle(styleRules.textAlign === 'center')}
              onClick={() => onChangeAlignment('center')}
            >
              <FontAwesomeIcon icon={icon({ name: 'align-center', style: 'light' })} style={{ fontSize: 14 }} />
            </div>
            <div
              title={t('right-alignment')}
              style={alignButtonStyle(styleRules.textAlign === 'right')}
              onClick={() => onChangeAlignment('right')}
            >
              <FontAwesomeIcon icon={icon({ name: 'align-right', style: 'light' })} style={{ fontSize: 14 }} />
            </div>
          </>
        )}
      </div>
  );
}

export const BlockTextStyle = ({ layout, ...props }) => (
  <Form.Item {...layout} label='Style du texte'>
    <TextStyleButtons {...props} />
  </Form.Item>
);

export const BlockTextColor = ({ onChangeColor, styleRules, colors, layout }) => {
  return (
    <Form.Item {...layout} label='Couleur du texte'>
      <ColorPaletteButton
        title='Couleur du texte'
        value={styleRules?.color}
        colors={colors}
        onChange={onChangeColor}
      />
    </Form.Item>
  );
}

// Style propre au numéro d'un niveau. Champ vide : il reprend celui du titre.
const TitleIndexStyleFields = ({ indexStyle, titleFontSize, colors, onChange }) => (
  <>
    <Form.Item {...modalFieldLayout} label="Police du numéro">
      <FontPicker
        allowClear
        width={MODAL_FIELD_WIDTH}
        placeholder="Police du titre"
        forcedValue={indexStyle.fontFamily || undefined}
        onChange={(fontFamily) => onChange({ fontFamily })}
      />
    </Form.Item>
    <Form.Item {...modalFieldLayout} label="Taille du numéro">
      <InputNumber
        value={indexStyle.fontSize}
        placeholder={String(titleFontSize ?? '')}
        min={minFontSize}
        max={maxTitleFontSize}
        onChange={(fontSize) => onChange({ fontSize: fontSize ?? null })}
        addonAfter={unitName}
        style={{ width: 150, marginLeft: '5px' }}
      />
    </Form.Item>
    <Form.Item
      {...modalFieldLayout}
      label="Épaisseur des caractères"
      // fontsUrlBuilder ne charge que ces graisses (200, 400, 700) : une autre
      // (600…) serait rendue avec la plus proche.
      tooltip="Seules épaisseurs chargées pour les polices du document"
    >
      <Select
        value={indexStyle.fontWeight}
        onChange={(fontWeight) => onChange({ fontWeight })}
        style={{ width: 150, marginLeft: '5px' }}
        options={[
          { label: 'Celle du titre', value: '' },
          { label: 'Fine', value: 200 },
          { label: 'Normale', value: 400 },
          { label: 'Grasse', value: 700 },
        ]}
      />
    </Form.Item>
    <Form.Item {...modalFieldLayout} label="Rendu du numéro">
      <Select
        value={indexStyle.outline ? 'outline' : 'fill'}
        onChange={(value) => onChange({ outline: value === 'outline' })}
        style={{ width: 150, marginLeft: '5px' }}
        options={[
          { label: 'Plein', value: 'fill' },
          { label: 'Contour', value: 'outline' },
        ]}
      />
    </Form.Item>
    {indexStyle.outline && (
      <Form.Item {...modalFieldLayout} label="Épaisseur du contour">
        <InputNumber
          value={indexStyle.outlineWidth}
          min={1}
          max={20}
          onChange={(outlineWidth) => onChange({
            outlineWidth: outlineWidth ?? DEFAULT_TITLE_INDEX_STYLE.outlineWidth,
          })}
          addonAfter={unitName}
          style={{ width: 150, marginLeft: '5px' }}
        />
      </Form.Item>
    )}
    <Form.Item
      {...modalFieldLayout}
      label={indexStyle.outline ? 'Couleur du contour' : 'Couleur du numéro'}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ColorPaletteButton
          title="Couleur du numéro"
          icon={
            <p
              className='font-color-indicator'
              style={{ ...iconButtonStyle(), fontSize: 14, '--font-color-indicator': indexStyle.color?.style }}
            >
              N°
            </p>
          }
          value={indexStyle.color}
          colors={colors}
          onChange={(label) => onChange({
            color: colors.find((color) => color.label === label) ?? null,
          })}
        />
        {/* La palette n'a pas de quoi se vider : sans ce retour, un numéro ne
            pourrait plus reprendre la couleur de son titre une fois coloré. */}
        {indexStyle.color && (
          <Button type="link" size="small" onClick={() => onChange({ color: null })}>
            Couleur du titre
          </Button>
        )}
      </div>
    </Form.Item>
    <Form.Item {...modalFieldLayout} label="Opacité du numéro">
      <InputNumber
        value={indexStyle.opacity}
        min={0}
        max={100}
        onChange={(opacity) => onChange({ opacity: opacity ?? 100 })}
        addonAfter="%"
        style={{ width: 150, marginLeft: '5px' }}
      />
    </Form.Item>
  </>
);

// Rendu du texte du titre, mêmes réglages que ceux du numéro.
const TitleTextEffectFields = ({ textEffect, onChange }) => (
  <>
    <Form.Item {...modalFieldLayout} label="Rendu du texte">
      <Select
        value={textEffect.outline ? 'outline' : 'fill'}
        onChange={(value) => onChange({ outline: value === 'outline' })}
        style={{ width: 150, marginLeft: '5px' }}
        options={[
          { label: 'Plein', value: 'fill' },
          { label: 'Contour', value: 'outline' },
        ]}
      />
    </Form.Item>
    {textEffect.outline && (
      <Form.Item {...modalFieldLayout} label="Épaisseur du contour">
        <InputNumber
          value={textEffect.outlineWidth}
          min={1}
          max={20}
          onChange={(outlineWidth) => onChange({
            outlineWidth: outlineWidth ?? DEFAULT_TITLE_TEXT_EFFECT.outlineWidth,
          })}
          addonAfter={unitName}
          style={{ width: 150, marginLeft: '5px' }}
        />
      </Form.Item>
    )}
    <Form.Item {...modalFieldLayout} label="Opacité du texte">
      <InputNumber
        value={textEffect.opacity}
        min={0}
        max={100}
        onChange={(opacity) => onChange({ opacity: opacity ?? 100 })}
        addonAfter="%"
        style={{ width: 150, marginLeft: '5px' }}
      />
    </Form.Item>
  </>
);

const TitleConfiguration = ({
  onChangeWeight,
  onChangeColor,
  onChangeFontStyle,
  onChangeTextDecoration,
  onChangeFontSize,
  onChangeVisibility,
  onChangeBackgroundColor,
  onChangeBorderColor,
  onChangeBorderPlace,
  onChangeAlignment,
  styleRules,
  title,
  level,
  onChangeType,
  titleType,
  type,
  onChangeMarginTop,
  onChangeMarginBottom,
  onChangeSkipLineAfter,
  onChangeShowPageNumber,
  onChangePageLeader,
  onChangeVerticalAlignment,
  onChangeTextOffset,
  onChangeIndexStyle,
  onChangeTextEffect,
}) => {
  const { t } = useTranslation();
  const { configuration } = useStore(selector);
  const numberedLevels = numberedTitleLevels(configuration);
  const titleLevel = headerIndentation[titleType];
  // Niveau sans numéro : plus de disposition du numéro (alignement vertical,
  // décalage du texte) à régler, il n'y a pas de numéro à placer.
  const indexHidden = !numberedLevels[titleLevel - 1];
  // Le format, lui, sert encore : le compteur d'un niveau sans numéro reste
  // dans la chaîne de ses sous-titres numérotés (le « 1 » de « I.1.1 »). Il ne
  // devient inutile que dans deux cas : ce niveau précède le premier niveau
  // numéroté, son compteur sortant alors de la chaîne (cf. displayTitleIndex),
  // ou aucun niveau plus profond n'est numéroté.
  const formatUnused =
    indexHidden &&
    (titleLevel - 1 < numberedLevels.indexOf(true) ||
      !numberedLevels.slice(titleLevel).some(Boolean));
  const indexStyle = { ...DEFAULT_TITLE_INDEX_STYLE, ...styleRules?.indexStyle };
  const changeHandler = {
    fontWeight: onChangeWeight,
    fontStyle: onChangeFontStyle,
    textDecoration: onChangeTextDecoration,
  }

  // Seuls les titres du document ont un numéro à régler : l'onglet Sommaire
  // reprend le leur, sa carte garde donc une colonne unique.
  const showNumberColumn = type === 'title';
  // Drawer de configuration compact (petit écran, cf. ConfigDrawer) : trop
  // étroit pour deux colonnes, le numéro passe sous le titre.
  const compact = useConfigCompact();
  const sideBySide = showNumberColumn && !compact;

  return (
    <div style={{
      border: '1px solid lightgray',
      borderRadius: '5px',
      padding: '1rem',
      margin: '1rem 0',
    }}>
      {/* Titres : ni aperçu (page A4 à droite du Drawer, ConfigPagePreview)
          ni intitulé (l'onglet du niveau le porte). */}
      {type !== 'title' && (
        <Form.Item
          {...modalFieldLayout}
          label={
            <div style={{ fontSize: 16, fontWeight: 'bold' }}>
              {`Titres de niveau ${level}`}
            </div>
          }
        >
        </Form.Item>
      )}
      {onChangeVisibility && level !== '1' && (
        <Form.Item
          {...modalFieldLayout}
          label="Visibilité du titre"
          name={`${type}-${level}-visibility`}
        >
          <Checkbox
            checked={styleRules.visibility !== ''}
            onChange={onChangeVisibility}
          >
          </Checkbox>
        </Form.Item>
      )}
      <Row gutter={24}>
        <Col span={sideBySide ? 13 : 24}>
          {/* Intitulé seulement en face de celui du numéro : seul, au-dessus
              d'une colonne unique, il ne distinguerait rien. */}
          {showNumberColumn && <FieldGroupTitle>Style du titre</FieldGroupTitle>}
          {
            (type === 'title' || (type === 'summary' && styleRules.visibility === 'visible')) &&
            <>
              <Form.Item
                {...modalFieldLayout}
                label="Police du titre"
                name={`${type}-${level}-font`}
              >
                <TitleFontSelector selectorType={titleType} currentTab={type} />
              </Form.Item>
              <Form.Item
                {...modalFieldLayout}
                label={t('font-size')}
                name={`${type}-${level}-font-size`}
                initialValue={styleRules.fontSize}
              >
                <InputNumber
                  min={minFontSize}
                  // Composant partagé avec l'onglet Sommaire : seuls les titres montent à 200.
                  max={type === 'title' ? maxTitleFontSize : maxFontSize}
                  onChange={onChangeFontSize}
                  addonAfter={unitName}
                  style={{ width: 150, marginLeft: '5px' }}
                />
              </Form.Item>
              <BlockTextStyle
                layout={modalFieldLayout}
                rules={styleRules}
                changeHandler={changeHandler}
                showAlignment={type === 'title'}
                onChangeAlignment={onChangeAlignment}
              />
              <BlockTextColor
                layout={modalFieldLayout}
                styleRules={styleRules}
                colors={configuration.colors}
                onChangeColor={onChangeColor}
              />
              {onChangeTextEffect && (
                <TitleTextEffectFields
                  textEffect={{ ...DEFAULT_TITLE_TEXT_EFFECT, ...styleRules.textEffect }}
                  onChange={onChangeTextEffect}
                />
              )}
            </>
          }
          {
            onChangeBorderPlace && (
              <Form.Item
                {...modalFieldLayout}
                label="Affichage de la bordure"
              >
                {['top', 'bottom', 'left', 'right'].map((border) => (
                  <Form.Item
                    name={`${type}-${level}-border-${border}`}
                    style={{
                      display: 'inline-block',
                      marginBottom: '0px',
                    }}
                  >
                    <Button
                      key={`border-${border}`}
                      style={{ marginRight: marginOnPageButton, marginBottom: 0 }}
                      onClick={onChangeBorderPlace(title, border)}
                      className={`RichEditor-styleButton ${styleRules.borders[border]
                        ? '  RichEditor-activeButton'
                        : ''
                        }`}
                      icon={borderIcons[border]}
                    />
                  </Form.Item>
                ))}

                <Form.Item
                  name={`${type}-${level}-border-all`}
                  style={{
                    display: 'inline-block',
                    marginBottom: '0px'
                  }}
                >
                  <Button
                    className={`RichEditor-styleButton ${styleRules.borders.right === true &&
                      styleRules.borders.left === true &&
                      styleRules.borders.top === true &&
                      styleRules.borders.bottom === true
                      ? '  RichEditor-activeButton'
                      : ''
                      }`}
                    onMouseDown={onChangeBorderPlace(title, 'all')}
                    icon={<BorderOuterOutlined />}
                  />
                </Form.Item>
              </Form.Item>
            )
          }
          {
            onChangeBorderColor && (
              <Form.Item {...modalFieldLayout} label="Couleur des bordures">
                <ColorPaletteButton
                  title="Couleur des bordures"
                  icon={<DuotoneLightIcon name='border-outer' style={iconButtonStyle()} />}
                  colorInIcon='primary'
                  value={styleRules.borders.color}
                  colors={configuration.colors}
                  onChange={onChangeBorderColor}
                />
              </Form.Item>
            )
          }
          {
            onChangeBackgroundColor && (
              <Form.Item {...modalFieldLayout} label="Couleur de fond">
                <ColorPaletteButton
                  title="Couleur de fond"
                  icon={<DuotoneLightIcon name='fill-drip' style={iconButtonStyle()} />}
                  value={styleRules.backgroundColor}
                  colors={configuration.colors}
                  onChange={onChangeBackgroundColor}
                />
              </Form.Item>
            )
          }
          <Form.Item
            {...modalFieldLayout}
            label={'Marge supérieure'}
          >
            <InputNumber value={styleRules.marginTop}
              onChange={onChangeMarginTop}
              min={0}
              max={60}
              addonAfter={unitName}
              style={{ width: 150 }}
            />
          </Form.Item>
          <Form.Item
            {...modalFieldLayout}
            label={'Marge inférieure'}
          >
            <InputNumber value={styleRules.marginBottom}
              onChange={onChangeMarginBottom}
              min={0}
              max={60}
              addonAfter={unitName}
              style={{ width: 150 }}
            />
          </Form.Item>
          {/* Le blanc d'une ligne s'ajoute à la marge inférieure, qui reste là pour
              l'ajuster. Deux lignes séparées par ce blanc et par la marge
              supérieure de la suivante ne les cumulent pas : les marges fusionnent,
              la plus grande gagne. */}
          {onChangeSkipLineAfter && (
            <Form.Item {...modalFieldLayout} label="Sauter une ligne après ce titre">
              <Checkbox
                checked={!!styleRules.skipLineAfter}
                onChange={onChangeSkipLineAfter}
              />
            </Form.Item>
          )}
          {onChangeShowPageNumber && (
            <Form.Item {...modalFieldLayout} label="Afficher le n° de page">
              <Checkbox
                checked={styleRules.showPageNumber !== false}
                onChange={onChangeShowPageNumber}
              />
            </Form.Item>
          )}
          {/* Sans n° de page, la liaison ne relierait rien. */}
          {onChangePageLeader && styleRules.showPageNumber !== false && (
            <Form.Item {...modalFieldLayout} label="Liaison entre le titre et le n° de page">
              <Select
                value={styleRules.pageLeader ?? 'none'}
                onChange={onChangePageLeader}
                style={{ width: 150 }}
                options={[
                  { label: 'Aucune', value: 'none' },
                  { label: 'Pointillés', value: 'dotted' },
                  { label: 'Trait', value: 'solid' },
                ]}
              />
            </Form.Item>
          )}
        </Col>
        {showNumberColumn && (
          // Réglages du numéro : ils vivaient dans un volet repliable, à ouvrir
          // pour chaque niveau. En colonne, un niveau se lit d'un seul coup
          // d'œil, son titre à gauche et son numéro à droite.
          <Col
            span={sideBySide ? 11 : 24}
            style={sideBySide
              ? { borderLeft: `1px solid ${BORDER_GRAY}` }
              : { borderTop: `1px solid ${BORDER_GRAY}`, paddingTop: 16, marginTop: 8 }}
          >
            <FieldGroupTitle>Numéro du titre</FieldGroupTitle>
            <Form.Item
              {...modalFieldLayout}
              label="Format de la numérotation"
              name={`${type}-${level}-format`}
              initialValue={styleRules.typeTitle}
            >
              <Select
                disabled={formatUnused}
                onChange={onChangeType}
                style={{
                  maxWidth: selectorMaxWidth,
                  width: '90px',
                }}
                options={[
                  { label: 'IV', value: 'romain' },
                  { label: '1', value: 'chiffre' },
                  { label: '01', value: 'chiffreZero' },
                  { label: '001', value: 'chiffreDoubleZero' },
                  { label: 'A', value: 'letterMaj' },
                  { label: 'a', value: 'letterMin' },
                ]}
              >
              </Select>
            </Form.Item>
            {/* Pas de `name` : champs pilotés par le store (cf. titleTab). */}
            {!indexHidden && (
              <>
                <Form.Item
                  {...modalFieldLayout}
                  label="Alignement vertical"
                  tooltip="Position du numéro face à un titre sur plusieurs lignes"
                >
                  <Select
                    value={styleRules.verticalAlignment || 'baseline'}
                    onChange={onChangeVerticalAlignment}
                    style={{ width: 150, marginLeft: '5px' }}
                    options={[
                      { label: 'Ligne de base', value: 'baseline' },
                      { label: 'Haut', value: 'top' },
                      { label: 'Centre', value: 'center' },
                      { label: 'Bas', value: 'bottom' },
                    ]}
                  />
                </Form.Item>
                <Form.Item
                  {...modalFieldLayout}
                  label="Décalage du texte"
                  tooltip="Négatif : le texte se rapproche du numéro jusqu'à le chevaucher"
                >
                  <InputNumber
                    value={styleRules.textOffset ?? 0}
                    min={-pageWidth}
                    max={pageWidth}
                    onChange={onChangeTextOffset}
                    addonAfter={unitName}
                    style={{ width: 150, marginLeft: '5px' }}
                  />
                </Form.Item>
                {onChangeIndexStyle && (
                  <>
                    {/* Alignée sur les champs qu'elle commente, pas sur le bord
                        de la colonne où elle flottait entre deux rangées. */}
                    <div style={{
                      color: '#8c8c8c',
                      fontSize: 12,
                      margin: '1rem 0',
                      marginLeft: MODAL_LABEL_WIDTH,
                    }}>
                      Champ vide : style du titre
                    </div>
                    <TitleIndexStyleFields
                      indexStyle={indexStyle}
                      titleFontSize={styleRules.fontSize}
                      colors={configuration.colors}
                      onChange={onChangeIndexStyle}
                    />
                  </>
                )}
              </>
            )}
          </Col>
        )}
      </Row>
    </div >
  );
};

export default TitleConfiguration;
